import { DisplayField, StepperContext } from '@digitools/honest-ui';
import React, { useContext } from 'react';
import useAsDataContext from '../../hooks/useAsDataContext';
import { phoneMask } from 'src/utils/validationUtils';
import useTranslate from 'src/hooks/useTranslate';
import { ReviewText } from '../../constants/ReviewText';
import { AS_COUNTRIES } from '../../constants/Constants';
// DisplayField entries only take label/value, so data-private and test ids go on a span inside the value.
// Empty values render blank; DisplayItem keeps the field's height.
const privateValue = (value, testId) => (<span data-private={true} data-testid={testId}>{value}</span>);
const trimmed = (value) => value?.trim() || undefined;
const ReviewAboutYou = ({ displayButton }) => {
    const { claimantData } = useAsDataContext();
    const stepper = useContext(StepperContext);
    const { t } = useTranslate();
    const isCountryUS = claimantData?.empCtryCde === 'USA';
    const isCountryCanada = claimantData?.empCtryCde === 'CAN';
    const selectedCountry = AS_COUNTRIES.find(country => country.value === claimantData?.empCtryCde);
    // Every section edits the About You step; ViewSubmission passes displayButton={false} to hide Edit
    const editProps = displayButton
        ? {
            onEdit: () => { stepper.edit(1); },
            // Passed explicitly: the app wraps honest-react's LanguageProvider, not honest-ui's, so the default would stay English
            editLabel: t(ReviewText.editButton),
        }
        : {};
    const locationFields = [
        { label: t(ReviewText.countryLabel), value: privateValue(selectedCountry?.label) },
        ...((isCountryUS || isCountryCanada)
            ? [{
                    label: isCountryUS ? t(ReviewText.stateLabel) : t(ReviewText.provinceLabel),
                    value: privateValue(trimmed(claimantData?.empState)),
                }]
            : []),
        { label: t(ReviewText.residentialInformation.cityQuestionLabel), value: privateValue(trimmed(claimantData?.empCity)) },
        { label: t(ReviewText.residentialInformation.zipQuestionLabel), value: privateValue(trimmed(claimantData?.empZip)) },
    ];
    return (<>
      <div data-testid='aboutYouReview'>
        <DisplayField title={t(ReviewText.personalInformation.personalInfoHeading)} headingLevel='h3' className='hui-mb-lg' {...editProps} rows={[
            {
                columns: 3,
                fields: [
                    { label: t(ReviewText.personalInformation.firstNameQuestionLabel), value: privateValue(trimmed(claimantData?.empFirstName), 'empFirstName') },
                    { label: t(ReviewText.personalInformation.middleInitialQuestionLabel), value: privateValue(trimmed(claimantData?.empMiName), 'empMiName') },
                    { label: t(ReviewText.personalInformation.lastNameQuestionLabel), value: privateValue(trimmed(claimantData?.empLastName), 'empLastName') },
                ],
            },
            {
                columns: 1,
                fields: [
                    { label: t(ReviewText.personalInformation.dateOfBirthQuestionLabel), value: privateValue(trimmed(claimantData?.empBthDte), 'empBthDte') },
                ],
            },
        ]}/>

        <DisplayField title={t(ReviewText.contactInformation.contactInfoHeading)} headingLevel='h3' className='hui-mb-lg' {...editProps} columns={2} fields={[
            {
                label: t(ReviewText.contactInformation.preferredPhoneQuestionLabel),
                value: privateValue(claimantData?.empPhoneNum?.trim() ? phoneMask(claimantData.empPhoneNum) : undefined, 'empPhoneNum'),
            },
            { label: t(ReviewText.contactInformation.preferredEmailAddressQuestionLabel), value: privateValue(trimmed(claimantData?.empEmail), 'empEmail') },
        ]}/>

        <DisplayField title={t(ReviewText.residentialInformation.residentialInfoHeading)} headingLevel='h3' className='hui-mb-lg' {...editProps} rows={[
            {
                columns: 2,
                fields: [
                    { label: t(ReviewText.residentialInformation.address1), value: privateValue(trimmed(claimantData?.empLine1Addr)) },
                    { label: t(ReviewText.residentialInformation.address2), value: privateValue(trimmed(claimantData?.empLine2Addr)) },
                ],
            },
            // State/province only shows for US and Canada, so the row drops to 3 columns without it
            { columns: locationFields.length === 4 ? 4 : 3, fields: locationFields },
        ]}/>
      </div>
    </>);
};
export default ReviewAboutYou;
